import { useCallback, useEffect, useMemo } from 'react';

import { useDispatch, useEffectOnUpdate, useSelector } from '_common/hooks';
import { SharedService } from '_common/services';
import { setCurrentAppPage } from 'App/redux/appSlice';
import { navigateToMyFiles } from 'router/navigation';

import { listObjects } from '_common/components/Table/TableSlice';
import { toggleInfoPanel } from 'Shared/redux/SharedListPageSlice';
import { setActiveTab, setTriggerPage } from 'Search/redux/SearchPageSlice';
import { useFilterSelector } from '_common/components/Filters/FilterSlice';

import {
  InformationPanel,
  ErrorView,
  IntlErrorBoundary,
  EmptyFilteredState,
  FilterDisplay,
  ObjectPreview,
  TableColumnsToggle,
} from '_common/components';

import Header from './Header/Header';
import Table from './Table/Table';

import styles from './SharedListPage.module.scss';

const PAGE_IDENTITY = 'shared';

const SharedListPage = () => {
  const dispatch = useDispatch();
  const { selectFilterParams } = useFilterSelector();

  const infoPanelOpen = useSelector((state) => state.shared.infoPanelOpen);
  const tableListLength = useSelector((state) => state.table.identity.shared.list.length);
  const error = useSelector((state) => state.app.error);
  const hasError = useMemo(
    () => error.status === 400 || error.status === 403 || error.status === 404,
    [error.status],
  );

  const filterParams = useSelector((state) => selectFilterParams(state, state.filters.shared));
  const filters = useMemo(() => {
    return {
      ...JSON.parse(localStorage.getItem(PAGE_IDENTITY) ?? '{}'),
      ...filterParams,
    };
  }, [filterParams]);

  //#region Table object listing handlers
  const fetchObjects = useCallback(async (parameters: any) => {
    const resp = await new SharedService().listShared(parameters);
    return resp;
  }, []);

  useEffect(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, []);

  useEffectOnUpdate(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'FILTER',
        request: { offset: 0, ...filters },
      }),
    );
  }, [filters]);

  //#endregion

  useEffect(() => {
    dispatch(setCurrentAppPage('/shared'));
    dispatch(setTriggerPage('shared'));
    dispatch(setActiveTab('shared'));
  }, []);

  const handleCloseInfoPanel = () => {
    dispatch(toggleInfoPanel());
  };

  /**
   * render content view
   */
  const renderContentView = () => {
    // check for forbidden view
    if (hasError) {
      return <ErrorView error={error} onClick={navigateToMyFiles} />;
    }

    //If has filters and no matching objects to display
    if (filterParams.filter_fields.length > 0 && tableListLength === 0) {
      return <EmptyFilteredState identity="shared" />;
    }

    return (
      <div className={styles.content}>
        <Table fetchObjects={fetchObjects} />
        <InformationPanel
          isOpen={infoPanelOpen}
          onClose={handleCloseInfoPanel}
          testId="shared-information-panel"
        />
      </div>
    );
  };

  return (
    <>
      <div className={styles.root}>
        <div className={styles.header}>
          <IntlErrorBoundary size="s" style={{ margin: '0 0 0 7rem' }}>
            <Header hasError={hasError} />
          </IntlErrorBoundary>
        </div>
        <div className={styles.headerFooter}>
          <div style={{ flex: 1 }}>
            <FilterDisplay identity="shared" margin={'0 0 0 6rem'} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', userSelect: 'none' }}>
            <TableColumnsToggle table="explorer" />
          </div>
        </div>
        {renderContentView()}
        <ObjectPreview />
      </div>
    </>
  );
};

export default SharedListPage;
